import type { Theme } from "../types/Theme";
import { foregroundColor, darken } from "./Mixins";
import {
    MODERN_GRID_ROW_BORDER_COLOR,
    MODERN_GRID_ROW_HOVER_BACKGROUND,
    MODERN_GRID_SUBHEADER_MUTED,
} from "./gridThemeTokens";
import { shouldUseModernTheme } from "./navigationButtonThemeColors";

export const MODERN_FILE_UPLOAD_DROPZONE_PADDING_Y_PX = 32;
export const MODERN_FILE_UPLOAD_DROPZONE_PADDING_X_PX = 16;
export const MODERN_FILE_UPLOAD_DROPZONE_GAP_PX = 10;
export const MODERN_FILE_UPLOAD_BORDER_WIDTH_PX = 2;
export const MODERN_FILE_UPLOAD_ICON_BOX_SIZE_PX = 40;
export const MODERN_FILE_UPLOAD_ICON_BOX_RADIUS_PX = 8;
export const MODERN_FILE_UPLOAD_PROGRESS_HEIGHT_PX = 4;
export const MODERN_FILE_UPLOAD_PROGRESS_TRANSITION_MS = 300;
export const MODERN_FILE_UPLOAD_ACTION_BUTTON_SIZE_PX = 28;
export const MODERN_FILE_UPLOAD_ROW_GAP_PX = 12;
export const MODERN_FILE_UPLOAD_ROW_MARGIN_TOP_PX = 8;
export const MODERN_FILE_UPLOAD_BROWSE_BUTTON_HEIGHT_PX = 40;
export const MODERN_FILE_UPLOAD_BROWSE_BUTTON_PADDING_X_PX = 32;
/** Semi-transparent black overlay applied on top of the browse button fill on hover. */
export const MODERN_FILE_UPLOAD_BROWSE_BUTTON_HOVER_OVERLAY = "#0000001a";
export const MODERN_FILE_UPLOAD_BORDER_INTERACTIVE = "#8A8A8A";
export const MODERN_FILE_UPLOAD_DESTRUCTIVE_COLOR = "#CF455C";
export const MODERN_FILE_UPLOAD_FOREGROUND_COLOR = "#232323";

export function useModernFileUploadStyles(theme: Theme): boolean {
    return shouldUseModernTheme(theme);
}

export function resolveFileUploadDropzoneBorderColor(theme: Theme, isDragOver?: boolean): string {
    if (isDragOver) {
        return theme.PrimaryColor;
    }
    return MODERN_FILE_UPLOAD_BORDER_INTERACTIVE;
}

export function resolveFileUploadDropzoneBackground(isDragOver?: boolean): string {
    return isDragOver ? MODERN_GRID_ROW_HOVER_BACKGROUND : "transparent";
}

export function resolveFileUploadDropzoneIconColor(): string {
    return MODERN_FILE_UPLOAD_FOREGROUND_COLOR;
}

export function resolveFileUploadRowBorderColor(_theme: Theme, isComplete?: boolean): string {
    return isComplete ? MODERN_FILE_UPLOAD_BORDER_INTERACTIVE : MODERN_GRID_ROW_BORDER_COLOR;
}

export function resolveFileUploadIconBoxBackground(): string {
    return MODERN_GRID_ROW_HOVER_BACKGROUND;
}

export function resolveFileUploadMutedTextColor(): string {
    return MODERN_GRID_SUBHEADER_MUTED;
}

export function resolveFileUploadProgressFillColor(theme: Theme): string {
    return theme.PrimaryColor;
}

export function resolveFileUploadProgressTrackColor(): string {
    return MODERN_GRID_ROW_HOVER_BACKGROUND;
}

export function resolveFileUploadCompletedColor(theme: Theme): string {
    return theme.SecondaryColor;
}

export function resolveFileUploadDestructiveColor(theme: Theme): string {
    return theme.ErrorColor ?? MODERN_FILE_UPLOAD_DESTRUCTIVE_COLOR;
}

export function resolveFileUploadBrowseButtonBackground(theme: Theme): string {
    return theme.PrimaryColor;
}

export function resolveFileUploadBrowseButtonForeground(theme: Theme): string {
    const bg = resolveFileUploadBrowseButtonBackground(theme);
    return foregroundColor(theme, bg);
}

export function resolveFileUploadBrowseButtonRestBoxShadow(theme: Theme): string {
    const bg = resolveFileUploadBrowseButtonBackground(theme);
    return `0 0 0 ${theme.BorderWidth}px ${darken(bg, 0.5)} inset`;
}

export function resolveFileUploadBrowseButtonHoverBoxShadow(theme: Theme): string {
    const border = resolveFileUploadBrowseButtonRestBoxShadow(theme);
    return `${border}, inset 0 0 0 9999px ${MODERN_FILE_UPLOAD_BROWSE_BUTTON_HOVER_OVERLAY}`;
}

/** JSS-safe vertical + horizontal padding (react-jss expects px strings or [[y, x]]). */
export function modernFileUploadPaddingYx(yPx: number, xPx: number): string {
    return `${yPx}px ${xPx}px`;
}

/** JSS-safe uniform padding. */
export function modernFileUploadPadding(allPx: number): string {
    return `${allPx}px`;
}

/** JSS-safe top/right/bottom/left padding. */
export function modernFileUploadPaddingTrbl(
    topPx: number,
    rightPx: number,
    bottomPx: number,
    leftPx: number,
): string {
    return `${topPx}px ${rightPx}px ${bottomPx}px ${leftPx}px`;
}
